import { useMemo, useState } from 'react';
import { ChipFilterDefinition, ChipFilterState, useChipFilters } from '@/components/FilterChips/useChipFilters';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { ListRange, ListRangePresetId, rangeForPreset } from '@/components/Hub/listRange';

/**
 * The scope and the chips for one CI working queue, reset together when the tab
 * changes — mirroring how `PatientListPage` already resets sorting via its
 * `prevFilter` comparison. Carrying one tab's chips onto another's population
 * would silently mis-describe the count.
 *
 * The range is what the tab feeds into its query variables; the chips filter
 * whatever came back.
 */
export interface UsePatientListFiltersOptions<T> {
  /** The active tab. Changing it restores every default. */
  resetKey: string;
  chipDefinitions: ChipFilterDefinition<T>[];
  /** Pre-consultation may want a wider default than post-consultation. */
  defaultPreset?: ListRangePresetId;
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export interface PatientListFilters<T> {
  range: ListRange;
  presetId: ListRangePresetId;
  /** A preset owns its range; only `custom` takes one from the caller. */
  setRange: (presetId: ListRangePresetId, customRange?: ListRange) => void;
  chips: ChipFilterState<T>;
}

export function usePatientListFilters<T>({
  resetKey,
  chipDefinitions,
  defaultPreset = 'last-30',
  now,
}: UsePatientListFiltersOptions<T>): PatientListFilters<T> {
  const today = useCalendarDay(useNow(now));
  const [presetId, setPresetId] = useState<ListRangePresetId>(defaultPreset);
  const [customRange, setCustomRange] = useState<ListRange>({});
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  if (previousResetKey !== resetKey) {
    setPreviousResetKey(resetKey);
    setPresetId(defaultPreset);
    setCustomRange({});
  }

  // Derived, not stored: a preset's range is a function of the day, and the
  // day moves. Holding `rangeForPreset()` in state froze "last 30 days" at
  // whatever day the tab was opened, so a queue left open overnight still
  // ended yesterday (BAR-2708). Keyed on the calendar day, so the query
  // variables keep their identity between ticks and change once, at midnight.
  const range = useMemo(
    () => (presetId === 'custom' ? customRange : rangeForPreset(presetId, today)),
    [presetId, customRange, today]
  );

  const chips = useChipFilters(chipDefinitions, resetKey);

  return {
    range,
    presetId,
    setRange: (nextPreset: ListRangePresetId, nextCustomRange: ListRange = {}) => {
      setPresetId(nextPreset);
      setCustomRange(nextPreset === 'custom' ? nextCustomRange : {});
    },
    chips,
  };
}
